import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Play, Pause, SkipBack, SkipForward, Shuffle, Repeat, Repeat1, Download, Volume2 } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { DEFAULT_COVER, formatTime, bgStyle } from "@/lib/format";

export function FullPlayer() {
  const {
    fullOpen, setFullOpen, currentSong, isPlaying, togglePlay, playNext, playPrev,
    progress, duration, seek, volume, changeVolume, shuffle, setShuffle, repeat, setRepeat,
    downloadSong, bg, t,
  } = useApp();

  const cycleRepeat = () => setRepeat(repeat === "off" ? "all" : repeat === "all" ? "one" : "off");

  return (
    <AnimatePresence>
      {fullOpen && currentSong && (
        <motion.div
          data-testid="full-player"
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 30, stiffness: 260 }}
          className="fixed inset-0 z-50 flex flex-col text-white"
        >
          <div className="absolute inset-0 -z-20" style={bgStyle(bg)} />
          <div className="absolute inset-0 -z-10" style={{ background: `linear-gradient(180deg, rgba(0,0,0,0.55), rgba(0,0,0,0.9))` }} />
          {currentSong.cover && (
            <div className="absolute inset-0 -z-30 opacity-60 blur-3xl scale-125" style={{ backgroundImage: `url(${currentSong.cover})`, backgroundSize: "cover", backgroundPosition: "center" }} />
          )}
          <div className="pointer-events-none absolute inset-0 noise -z-10" />

          <div className="mx-auto w-full max-w-md flex flex-col h-full px-6 pt-[max(env(safe-area-inset-top),20px)] pb-[max(env(safe-area-inset-bottom),24px)]">
            <div className="flex items-center justify-between py-2">
              <button data-testid="close-full-player" onClick={() => setFullOpen(false)} aria-label="close" className="h-10 w-10 grid place-items-center rounded-full bg-white/10 backdrop-blur">
                <ChevronDown className="h-6 w-6" />
              </button>
              <p className="text-xs uppercase tracking-[0.2em] text-white/60">{t("nowPlaying")}</p>
              <button data-testid="full-download-btn" onClick={() => downloadSong(currentSong)} aria-label={t("download")} className="h-10 w-10 grid place-items-center rounded-full bg-white/10 backdrop-blur">
                <Download className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 grid place-items-center py-4">
              <motion.img
                key={currentSong.id}
                src={currentSong.cover || DEFAULT_COVER}
                alt=""
                animate={{ scale: isPlaying ? 1 : 0.9 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
                className="w-[78vw] max-w-[340px] aspect-square rounded-2xl object-cover shadow-2xl shadow-black/70"
              />
            </div>

            <div className="mb-5">
              <h2 data-testid="full-title" className="font-display font-extrabold text-2xl tracking-tight truncate">{currentSong.title}</h2>
              <p className="text-white/60 truncate">{currentSong.artist}</p>
            </div>

            <div className="mb-4">
              <input
                data-testid="seek-bar"
                type="range"
                min={0}
                max={duration || 0}
                step="0.1"
                value={progress}
                onChange={(e) => seek(parseFloat(e.target.value))}
                className="z-range"
                style={{ background: `linear-gradient(to right, #ff3366 ${(duration ? (progress / duration) * 100 : 0)}%, rgba(255,255,255,0.16) 0%)` }}
              />
              <div className="flex justify-between text-xs text-white/50 mt-1.5">
                <span>{formatTime(progress)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            <div className="flex items-center justify-between mb-5">
              <button data-testid="shuffle-btn" onClick={() => setShuffle(!shuffle)} aria-label={t("shuffle")} className={`h-11 w-11 grid place-items-center rounded-full transition-colors ${shuffle ? "text-[#ff3366]" : "text-white/60"}`}>
                <Shuffle className="h-5 w-5" />
              </button>
              <button data-testid="prev-btn" onClick={playPrev} aria-label="previous" className="h-12 w-12 grid place-items-center text-white">
                <SkipBack className="h-8 w-8" fill="white" />
              </button>
              <button data-testid="full-play-btn" onClick={togglePlay} aria-label="play" className="h-[68px] w-[68px] grid place-items-center rounded-full bg-[#ff3366] text-white shadow-xl shadow-[#ff3366]/40 active:scale-95 transition-transform">
                {isPlaying ? <Pause className="h-8 w-8" fill="white" /> : <Play className="h-8 w-8 ml-1" fill="white" />}
              </button>
              <button data-testid="next-btn" onClick={playNext} aria-label="next" className="h-12 w-12 grid place-items-center text-white">
                <SkipForward className="h-8 w-8" fill="white" />
              </button>
              <button data-testid="repeat-btn" onClick={cycleRepeat} aria-label={t("repeat")} className={`h-11 w-11 grid place-items-center rounded-full transition-colors ${repeat !== "off" ? "text-[#ff3366]" : "text-white/60"}`}>
                {repeat === "one" ? <Repeat1 className="h-5 w-5" /> : <Repeat className="h-5 w-5" />}
              </button>
            </div>

            <div className="flex items-center gap-3 px-1">
              <Volume2 className="h-4 w-4 text-white/60 shrink-0" />
              <input
                data-testid="volume-bar"
                type="range"
                min={0}
                max={1}
                step="0.01"
                value={volume}
                onChange={(e) => changeVolume(parseFloat(e.target.value))}
                className="z-range thin"
                style={{ background: `linear-gradient(to right, rgba(255,255,255,0.8) ${volume * 100}%, rgba(255,255,255,0.16) 0%)` }}
              />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
